import { useCallback, useState } from 'react';
import { Node, Edge, Connection, addEdge, MarkerType } from 'reactflow';
import { WorkflowNodeType, isValidConnection, NODE_HANDLES } from '../workflowDefinitions';
import { WorkflowCanvasMode } from '../types';
import { 
  createPhaseNode, 
  createToolNode, 
  createPersonaNode, 
  createArtifactNode, 
  createConnectedNode, 
  createDependencyEdge 
} from '../utils/workflowNodeEdgeUtils';

export function useWorkflowOperations(
  nodes: Node[],
  setNodes: (nodes: Node[] | ((nds: Node[]) => Node[])) => void,
  edges: Edge[],
  setEdges: (edges: Edge[] | ((eds: Edge[]) => Edge[])) => void,
  mode: WorkflowCanvasMode,
  darkMode: boolean,
  onWorkflowChange?: (change: any) => void,
  readOnly: boolean = false
) {
  // Connection preview state
  const [connectionPreview, setConnectionPreview] = useState<{
    sourceId: string;
    sourceHandle: string;
    validTargets: string[];
  } | null>(null);

  // Smart connection feedback
  const onConnectStart = useCallback((event, { nodeId, handleId, handleType }) => {
    console.log('Connection started from:', nodeId, handleId);
    
    // Find all valid connection targets
    const sourceNode = nodes.find(n => n.id === nodeId);
    if (!sourceNode) return;
    
    const validTargets = nodes
      .filter(node => node.id !== nodeId)
      .filter(node => {
        // Check each handle of the target node
        const targetHandles = NODE_HANDLES[node.type as WorkflowNodeType] || [];
        return targetHandles.some(handle => {
          return isValidConnection(
            sourceNode.type as WorkflowNodeType,
            handleId,
            node.type as WorkflowNodeType,
            handle.id
          );
        });
      })
      .map(node => node.id);
    
    setConnectionPreview({
      sourceId: nodeId,
      sourceHandle: handleId,
      validTargets
    });
    
    // Update node styles to show valid targets
    setNodes(nodes => nodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        isValidConnectionTarget: validTargets.includes(node.id),
        isConnectionSource: node.id === nodeId
      }
    })));
  }, [nodes, setNodes]);

  const onConnectEnd = useCallback(() => {
    setConnectionPreview(null);
    
    // Reset node styles
    setNodes(nodes => nodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        isValidConnectionTarget: false,
        isConnectionSource: false
      }
    })));
  }, [setNodes]);

  // Get suggested connections for a node
  const getSuggestedConnections = useCallback((nodeId: string) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return [];
    
    const suggestions = [];
    
    if (node.type === 'phase') {
      // Suggest connecting to unconnected phases
      const unconnectedPhases = nodes
        .filter(n => n.type === 'phase' && n.id !== nodeId)
        .filter(n => !edges.some(e => e.source === nodeId && e.target === n.id));
      
      suggestions.push(...unconnectedPhases.map(n => ({
        type: 'phase_connection',
        target: n.id,
        label: `Connect to ${n.data.label}`,
        action: () => {
          // Auto-connect with appropriate handles
          if (onWorkflowChange) {
            onWorkflowChange({
              type: 'add_connection',
              source: nodeId,
              target: n.id,
              sourceHandle: 'output',
              targetHandle: 'input',
              dependencyType: 'flow'
            });
          }
        }
      })));
    }
    
    return suggestions;
  }, [nodes, edges, onWorkflowChange]);
  // Add a new node
  const addNode = useCallback((type: string, label: string, position: { x: number, y: number }) => {
    if (mode !== WorkflowCanvasMode.EDIT || readOnly) return;

    // Check if position is valid
    if (!position || typeof position.x !== 'number' || typeof position.y !== 'number') {
      console.error("Invalid position for new node:", position);
      // Provide a default position
      position = { x: 100, y: 100 };
    }

    console.log(`Adding new node at position:`, position);

    const newNode = createPhaseNode(
      type,
      label,
      position,
      mode,
      darkMode,
      onWorkflowChange,
      (nodeId, isExpanded) => {
        setNodes(nodes => nodes.map(node => 
          node.id === nodeId ? { ...node, data: { ...node.data, expanded: isExpanded } } : node
        ));
        
        // Notify about the change
        if (onWorkflowChange) {
          onWorkflowChange({
            type: 'update_phase',
            phaseId: nodeId,
            updates: { expanded: isExpanded }
          });
        }
      }
    );

    console.log(`Adding new node to nodes array:`, newNode);
    setNodes(nds => [...nds, newNode]);

    // Notify about the change
    if (onWorkflowChange) {
      console.log(`Notifying about new node creation`);
      onWorkflowChange({
        type: 'add_node',
        node: newNode
      });
    }
  }, [mode, readOnly, darkMode, onWorkflowChange, setNodes]);

  // Add a tool node
  const addToolNode = useCallback((position: { x: number, y: number }) => {
    if (mode !== WorkflowCanvasMode.EDIT || readOnly) return;

    const newNode = createToolNode(
      position,
      mode,
      darkMode,
      onWorkflowChange
    );

    console.log(`Adding new tool node to nodes array:`, newNode);
    setNodes(nds => [...nds, newNode]);

    // Notify about the change
    if (onWorkflowChange) {
      console.log(`Notifying about new tool node creation`);
      onWorkflowChange({
        type: 'add_tool_node',
        node: newNode
      });
    }
  }, [mode, readOnly, darkMode, onWorkflowChange, setNodes]);

  // Add a persona node
  const addPersonaNode = useCallback((position: { x: number, y: number }) => {
    if (mode !== WorkflowCanvasMode.EDIT || readOnly) return;

    const newNode = createPersonaNode(
      position,
      mode,
      darkMode,
      onWorkflowChange
    );

    console.log(`Adding new persona node to nodes array:`, newNode);
    setNodes(nds => [...nds, newNode]);

    // Notify about the change
    if (onWorkflowChange) {
      console.log(`Notifying about new persona node creation`);
      onWorkflowChange({
        type: 'add_persona_node',
        node: newNode
      });
    }
  }, [mode, readOnly, darkMode, onWorkflowChange, setNodes]);

  // Add this function to handle adding a persona to a phase
  const addPersonaToPhase = useCallback((sourceNodeId: string, phaseId: string, role: string = 'Contributor') => {
    if (mode !== WorkflowCanvasMode.EDIT || readOnly) return;

    // Find the persona node to get the persona ID
    const personaNode = nodes.find(node => node.id === sourceNodeId);
    const personaId = personaNode?.data?.personaId;
    
    console.log(`DETAILED DEBUG: Adding persona ${personaId} to phase ${phaseId} with role ${role}`);
    console.log(`DETAILED DEBUG: Persona node data:`, personaNode?.data);
    
    if (!personaId) {
      console.error('Persona ID not found');
      return;
    }
    
    // Find and update the phase
    setNodes(nodes => nodes.map(node => {
      if (node.id === phaseId) {
        // Make sure config and personas exist
        const config = node.data?.config || {};
        const personas = config.personas || [];
        
        // Check if this persona is already assigned
        const existingAssignment = personas.find(p => p.persona_id === personaId);
        
        console.log(`DETAILED DEBUG: Existing personas for phase ${phaseId}:`, personas);
        
        if (!existingAssignment) {
          // Add the persona assignment with full persona data
          const updatedConfig = {
            ...config,
            personas: [
              ...personas,
              {
                persona_id: personaId,
                role: role,
                persona_data: personaNode?.data?.personaData // Store full persona data
              }
            ]
          };
          
          console.log(`DETAILED DEBUG: Updated config for phase ${phaseId}:`, updatedConfig);
          
          return {
            ...node,
            data: {
              ...node.data,
              config: updatedConfig
            }
          };
        }
      }
      return node;
    }));
    
    // Add the connection to the edges
    setEdges(edges => [
      ...edges,
      {
        id: `e-${sourceNodeId}-${phaseId}`,
        source: sourceNodeId,
        target: phaseId,
        sourceHandle: 'persona_output',
        targetHandle: 'personas',
        type: 'smoothstep',
        animated: false,
        style: { stroke: '#aa00aa', strokeWidth: 2 },
        data: { type: 'persona_assignment' }
      }
    ]);
    
    // Notify about the change
    if (onWorkflowChange) {
      console.log(`DETAILED DEBUG: Notifying about persona assignment: ${personaId} -> ${phaseId}`);
      onWorkflowChange({
        type: 'add_persona_to_phase',
        personaNodeId: sourceNodeId,
        phaseId: phaseId,
        personaId: personaId,
        personaData: personaNode?.data?.personaData,
        role: role
      });
    }
  }, [mode, readOnly, nodes, setNodes, setEdges, onWorkflowChange]);

  // Add an artifact node
  const addArtifactNode = useCallback((position: { x: number, y: number }, isOutput: boolean = false) => {
    if (mode !== WorkflowCanvasMode.EDIT || readOnly) return;

    const newNode = createArtifactNode(
      position,
      isOutput,
      mode,
      darkMode,
      onWorkflowChange
    );

    console.log(`Adding new artifact node to nodes array:`, newNode);
    setNodes(nds => [...nds, newNode]);

    // Notify about the change
    if (onWorkflowChange) {
      console.log(`Notifying about new artifact node creation`);
      onWorkflowChange({
        type: 'add_artifact_node',
        node: newNode
      });
    }
  }, [mode, readOnly, darkMode, onWorkflowChange, setNodes]);

  // Delete a node
  const deleteNode = useCallback((nodeId: string) => {
    if (mode !== WorkflowCanvasMode.EDIT || readOnly) return;

    // Remove node
    setNodes(nds => nds.filter(node => node.id !== nodeId));

    // Remove connected edges
    setEdges(eds => eds.filter(
      edge => edge.source !== nodeId && edge.target !== nodeId
    ));

    // Notify about the change
    if (onWorkflowChange) {
      onWorkflowChange({
        type: 'delete_node',
        nodeId
      });
    }
  }, [mode, readOnly, onWorkflowChange, setNodes, setEdges]);

  // Add a connected node
  const addConnectedNode = useCallback((sourceNodeId: string, direction: 'right' | 'bottom' | 'left' | 'top') => {
    if (mode !== WorkflowCanvasMode.EDIT || readOnly) return;

    const result = createConnectedNode(
      sourceNodeId,
      direction,
      nodes,
      mode,
      darkMode,
      onWorkflowChange,
      (nodeId, isExpanded) => {
        setNodes(nodes => nodes.map(node => 
          node.id === nodeId ? { ...node, data: { ...node.data, expanded: isExpanded } } : node
        ));
        
        // Notify about the change
        if (onWorkflowChange) {
          onWorkflowChange({
            type: 'update_phase',
            phaseId: nodeId,
            updates: { expanded: isExpanded }
          });
        }
      }
    );

    if (result) {
      const { newNode, newEdge } = result;

      // Add node to graph
      setNodes(nds => [...nds, newNode]);

      // Add edge to graph
      setEdges(eds => [...eds, newEdge]);

      // Notify about the change
      if (onWorkflowChange) {
        onWorkflowChange({
          type: 'add_connected_node',
          sourceNodeId,
          newNodeId: newNode.id,
          direction,
          dependencyType: newEdge.data?.type
        });
      }
    }
  }, [mode, readOnly, nodes, darkMode, onWorkflowChange, setNodes, setEdges]);

  // Change dependency type
  const changeDependencyType = useCallback((edgeId: string, type: 'requires' | 'enhances' | 'blocks') => {
    if (mode !== WorkflowCanvasMode.EDIT || readOnly) return;

    setEdges(eds => eds.map(edge => {
      if (edge.id === edgeId) {
        return {
          ...edge,
          data: {
            ...edge.data,
            type
          },
          style: {
            ...edge.style,
            stroke: type === 'requires' ? '#ff0000' :
                    type === 'enhances' ? '#00aa00' :
                    type === 'blocks' ? '#aa0000' : '#888888'
          }
        };
      }
      return edge;
    }));

    // Notify about the change
    if (onWorkflowChange) {
      onWorkflowChange({
        type: 'change_dependency_type',
        edgeId,
        dependencyType: type
      });
    }
  }, [mode, readOnly, onWorkflowChange, setEdges]);

  // Delete edge
  const deleteEdge = useCallback((edgeId: string) => {
    if (mode !== WorkflowCanvasMode.EDIT || readOnly) return;

    setEdges(eds => eds.filter(edge => edge.id !== edgeId));

    // Notify about the change
    if (onWorkflowChange) {
      onWorkflowChange({
        type: 'delete_edge',
        edgeId
      });
    }
  }, [mode, readOnly, onWorkflowChange, setEdges]);

  // Handle connection between nodes
  const onConnect = useCallback((params: Connection) => {
    if (mode !== WorkflowCanvasMode.EDIT || readOnly) return;

    console.log('Connection params:', params);
    console.log(`Connecting from ${params.source} (handle: ${params.sourceHandle}) to ${params.target} (handle: ${params.targetHandle})`);

    // Get source and target nodes
    const sourceNode = nodes.find(node => node.id === params.source);
    const targetNode = nodes.find(node => node.id === params.target);
    
    if (!sourceNode || !targetNode) {
      console.error('Source or target node not found');
      return;
    }
    
    // Check if this is a valid connection
    const connectionRule = isValidConnection(
      sourceNode.type as WorkflowNodeType,
      params.sourceHandle,
      targetNode.type as WorkflowNodeType,
      params.targetHandle
    );
    
    if (!connectionRule) {
      console.warn('Invalid connection:', 
        sourceNode.type, '(', params.sourceHandle, ') ->', 
        targetNode.type, '(', params.targetHandle, ')');
      return;
    }
    
    // Always notify about the connection regardless of type
    if (onWorkflowChange) {
      onWorkflowChange({
        type: 'add_connection',
        source: params.source,
        target: params.target,
        sourceHandle: params.sourceHandle,
        targetHandle: params.targetHandle,
        dependencyType: connectionRule.connectionType,
        preservePositions: true // Add flag to indicate positions should be preserved
      });
    }
    
    // Handle different connection types based on the rule
    switch (connectionRule.connectionType) {
      case 'tool_assignment':
        // Tool -> Phase (tools pin) connection
        console.log('Creating Tool -> Phase (tools) connection');
            
        // Add the tool to the phase's config
        if (onWorkflowChange) {
          // Don't require toolId to be set - use the node ID as a fallback
          const toolId = sourceNode.data.toolId || sourceNode.id;
          const toolName = sourceNode.data.toolName || 'Unnamed Tool';
              
          onWorkflowChange({
            type: 'add_tool_to_phase',
            toolNodeId: sourceNode.id,
            phaseId: targetNode.id,
            toolId: toolId,
            toolData: {
              id: toolId,
              name: toolName,
              version: sourceNode.data.toolVersion || '1.0.0'
            }
          });
        }
        break;
        
      case 'persona_assignment':
        // Persona -> Phase (personas pin) connection
        console.log('Creating Persona -> Phase (personas) connection');
        
        // Add the persona to the phase's config
        if (onWorkflowChange) {
          onWorkflowChange({
            type: 'add_persona_to_phase',
            personaNodeId: sourceNode.id,
            phaseId: targetNode.id,
            personaId: sourceNode.data.personaId,
            personaData: sourceNode.data.personaData,
            role: 'Contributor' // Default role
          });
        }
        break;
        
      case 'input_artifact':
        // Input Artifact -> Phase (artifacts_in pin) connection
        console.log('Creating Input Artifact -> Phase connection');
        
        // Add the artifacts to the phase's input artifacts
        if (onWorkflowChange) {
          onWorkflowChange({
            type: 'add_input_artifacts_to_phase',
            artifactNodeId: sourceNode.id,
            phaseId: targetNode.id,
            artifacts: sourceNode.data.selectedArtifacts
          });
        }
        break;
        
      case 'output_artifact':
        // Phase (artifacts_out pin) -> Output Artifact connection
        console.log('Creating Phase -> Output Artifact connection');
        
        // Add the artifacts to the phase's output artifacts
        if (onWorkflowChange) {
          onWorkflowChange({
            type: 'add_output_artifacts_to_phase',
            artifactNodeId: targetNode.id,
            phaseId: sourceNode.id,
            artifacts: targetNode.data.selectedArtifacts
          });
        }
        break;
        
      case 'flow':
        // Phase -> Phase connection (flow)
        console.log('Creating Phase -> Phase flow connection');
        
        // Notify about the change
        if (onWorkflowChange) {
          onWorkflowChange({
            type: 'add_connection',
            source: params.source,
            target: params.target,
            sourceHandle: params.sourceHandle,
            targetHandle: params.targetHandle,
            dependencyType: 'flow'
          });
        }
        break;
    }
    
    // Create the edge with the correct style from the connection rule
    setEdges((eds) => {
      const newEdges = addEdge({
        ...params,
        type: 'smoothstep',
        data: { type: connectionRule.connectionType },
        style: connectionRule.style || {
          stroke: '#888888',
          strokeWidth: 2
        },
        markerEnd: {
          type: MarkerType.ArrowClosed
        }
      }, eds);
      
      return newEdges;
    });
  }, [mode, readOnly, onWorkflowChange, setEdges, nodes]);

  return {
    addNode,
    addToolNode,
    addPersonaNode,
    addArtifactNode,
    deleteNode,
    addConnectedNode,
    changeDependencyType,
    deleteEdge,
    onConnect,
    onConnectStart,
    onConnectEnd,
    getSuggestedConnections,
    connectionPreview
  };
}
